import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  CheckCircle,
  ArrowRight,
  Upload,
  BarChart3,
  FileCheck,
  Info,
  Zap,
  Crown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../hooks/useCurrency';
import { useAuth } from '../context/AuthContext';

const LandingPage = () => {
  const { t } = useTranslation();
  const currency = useCurrency();
  const { user } = useAuth();

  const analyserDest = '/analyser';
  const premiumDest = user ? '/tarifs' : '/inscription';

  const features = [
    { icon: Upload, title: t('landing.step1_title'), description: t('landing.step1_body') },
    { icon: BarChart3, title: t('landing.step2_title'), description: t('landing.step2_body') },
    { icon: FileCheck, title: t('landing.step3_title'), description: t('landing.step3_body') }
  ];

  const faqItems = [
    { question: t('landing.faq1_q'), answer: t('landing.faq1_a') },
    { question: t('landing.faq2_q'), answer: t('landing.faq2_a') },
    { question: t('landing.faq3_q'), answer: t('landing.faq3_a') },
    { question: t('landing.faq4_q'), answer: t('landing.faq4_a') }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24" data-testid="hero-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">

          <h1
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug font-bold text-zinc-900 mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="hero-title"
          >
            {t('landing.hero_title')}<br />
            <span className="text-blue-600">{t('landing.hero_subtitle')}</span>
          </h1>

          <p className="text-base sm:text-lg text-zinc-500 mb-8 max-w-lg mx-auto">
            {t('landing.hero_body')}
          </p>

          <div className="flex justify-center mb-3">
            <Link to={analyserDest} data-testid="cta-primary">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-8 py-4 text-lg shadow-lg shadow-blue-600/25">
                {t('landing.hero_cta')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

          <p className="text-sm text-zinc-400 mb-12">
            {t('landing.hero_badge')}
          </p>

          {/* Visual mockup */}
          <div className="max-w-sm mx-auto bg-white border border-zinc-200 rounded-2xl shadow-lg p-5 text-left">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-16 h-16 flex-shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 60 60">
                  <circle cx="30" cy="30" r="24" fill="none" stroke="#e4e4e7" strokeWidth="6"/>
                  <circle cx="30" cy="30" r="24" fill="none" stroke="#ea580c" strokeWidth="6"
                    strokeLinecap="round" strokeDasharray="79 151"/>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-base font-bold text-orange-600">52%</span>
                </div>
              </div>
              <div>
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 mb-1">{t('landing.mockup_status')}</span>
                <p className="text-xs text-zinc-500">{t('landing.mockup_job')}</p>
                <p className="text-xs text-red-600 font-medium mt-1">{t('landing.mockup_errors')}</p>
              </div>
            </div>
            <div className="space-y-2 border-t border-zinc-100 pt-3">
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span className="text-red-500">✕</span>
                <span>{t('landing.mockup_miss')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span className="text-red-500">✕</span>
                <span>{t('landing.mockup_exp')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span className="text-amber-500">!</span>
                <span className="font-medium">{t('landing.mockup_warn')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ATS Explanation Section */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="ats-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 mb-6">
            <Info className="h-7 w-7 text-blue-600" />
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug font-semibold text-zinc-900 mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t('landing.how_title')}
          </h2>
          <p className="text-lg text-zinc-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('landing.how_body')}
          </p>
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 md:p-8 text-left max-w-2xl mx-auto">
            <h3 className="font-semibold text-zinc-900 mb-4">{t('landing.how_detects')}</h3>
            <ul className="space-y-3">
              {['ats_1', 'ats_2', 'ats_3', 'ats_4'].map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-zinc-600">{t(`landing.${key}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-28 bg-white" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug font-semibold text-zinc-900"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t('landing.steps_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white border border-zinc-200 rounded-xl p-7 hover:shadow-md hover:border-zinc-300 transition-all duration-300"
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5">
                  <feature.icon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mb-3">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-zinc-600 leading-relaxed text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-28 bg-zinc-50" data-testid="pricing-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4 block">
              {t('landing.pricing_title')}
            </span>
            <h2
              className="text-2xl sm:text-3xl tracking-tight font-semibold text-zinc-900"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t('landing.pricing_sub')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Single analysis - HIGHLIGHTED */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 relative text-white shadow-xl shadow-blue-600/25">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-zinc-900 text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                <Zap className="h-3 w-3" />
                {t('landing.popular')}
              </div>
              <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t('landing.single_plan')}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold">{currency.format(currency.single)}</span>
                <span className="text-blue-200 ml-2 text-sm">{t('landing.single_sub_label')}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['single_f1', 'single_f2', 'single_f3', 'single_f4', 'single_f5'].map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-blue-100 text-sm">{t(`landing.${key}`)}</span>
                  </li>
                ))}
              </ul>
              <Link to={analyserDest}>
                <Button className="w-full bg-white hover:bg-zinc-100 text-blue-700 font-bold rounded-xl py-3 text-base">
                  {t('landing.single_cta_btn')}
                </Button>
              </Link>
              <p className="text-center text-blue-200 text-xs mt-3">{t('landing.single_note')}</p>
            </div>

            {/* Premium */}
            <div className="bg-white border-2 border-amber-300 rounded-2xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                <Crown className="h-3 w-3" />
                PREMIUM
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t('landing.premium_plan')}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-zinc-900">{currency.format(currency.premium)}</span>
                <span className="text-zinc-500 ml-2 text-sm">{t('landing.per_month')}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['premium_f1', 'premium_f2', 'premium_f3', 'premium_f4'].map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-600 text-sm">{t(`landing.${key}`)}</span>
                  </li>
                ))}
              </ul>
              <Link to={premiumDest}>
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl py-3 text-base shadow-lg shadow-amber-500/20">
                  {t('landing.premium_cta')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof / early adopters */}
      <section className="py-16 md:py-24 bg-white" data-testid="early-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="text-2xl sm:text-3xl font-semibold text-zinc-900 mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t('landing.early_title')}
          </h2>
          <p className="text-zinc-500 mb-8 max-w-lg mx-auto">
            {t('landing.early_body')}
          </p>
          <Link to={analyserDest}>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-8 py-4 text-base shadow-lg shadow-blue-600/20">
              {t('landing.hero_cta')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-zinc-50" data-testid="faq-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4 block">
              {t('landing.faq_title')}
            </span>
            <h2
              className="text-2xl sm:text-3xl tracking-tight font-semibold text-zinc-900"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t('landing.faq_sub')}
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-zinc-200 rounded-xl p-6"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-semibold text-zinc-900 mb-2 text-base">{item.question}</h3>
                <p className="text-zinc-600 text-sm leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 md:py-28 bg-zinc-900" data-testid="cta-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug font-semibold text-white mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t('landing.cta_title').split('\n').map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </h2>
          <p className="text-lg text-zinc-400 mb-8">
            {t('landing.cta_sub')}
          </p>
          <Link to={analyserDest} data-testid="cta-final">
            <Button className="bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl px-8 py-4 text-lg shadow-lg shadow-blue-600/30">
              {t('landing.cta_btn')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
